'use client';

import { notifyManager, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useSyncExternalStore } from 'react';

import type { SessionTransportState } from './session-controller';
import type { ListedWork } from './turn-activity';
import type { CodeSessionSummary, SessionResponse } from './types';

import { codeSessionOptions } from './queries';
import { isSessionListQuery, listsFor } from './runtime/session-lists';
import { getCodeSessionController } from './session-controller';
import { deriveTurnActivity, isSessionWorking, listedWork } from './turn-activity';
import { userMessageText } from './turn-prompt';

import { deliveredTurnStatus } from '@/lib/code-sessions/turn-utils';

/** Whether a session works, from its cached detail and this tab's transport; see `useSessionRunning`. */
function isSessionRunning(
  data: SessionResponse | undefined,
  transport: SessionTransportState,
  fallbackRunning: boolean,
  listed: CodeSessionSummary | undefined,
): boolean {
  // Unopened sessions only have their summary. Once detail is available, a
  // stale "running" summary must not make an idle session's held queue look
  // like active work.
  if (!data) {
    return fallbackRunning;
  }
  // A newer summary ends work or starts it, as it reads; a stale one still defers to detail.
  const listedVerdict = listedWork(listed, data.session);

  const delivered = new Set(
    (data.transcript?.messages ?? [])
      .filter((message) => message.type === 'user' && userMessageText(message).length > 0)
      .map((message) => message.id),
  );
  const activity = deriveTurnActivity(
    data.session.turns.map((turn) => ({
      ...turn,
      status: deliveredTurnStatus(turn, delivered),
      createdAt: Date.parse(turn.createdAt),
      startedAt: turn.startedAt === null ? undefined : Date.parse(turn.startedAt),
      endedAt: turn.endedAt === null ? null : Date.parse(turn.endedAt),
    })),
    transport.turnActivityEvents,
    transport.localDispatches,
    { listed: listedVerdict },
  );
  // A first prompt the agent has not taken yet is starting, which is work: the
  // row must not go idle between the sandbox's readiness and its delivery.
  return isSessionWorking(data.session.status, activity, listedVerdict);
}

/** The session's cached list row, kept current as the lists refetch; reading it fetches no list. */
export function useListedSession(sessionId: string | null): CodeSessionSummary | undefined {
  const client = useQueryClient();
  const subscribe = useCallback(
    (onChange: () => void) =>
      client.getQueryCache().subscribe(
        // Deferred as `useQuery` defers its own notifications: a list's `useQuery` can mount during
        // another component's render.
        notifyManager.batchCalls(({ query }) => {
          if (isSessionListQuery(query)) {
            onChange();
          }
        }),
      ),
    [client],
  );
  return useSyncExternalStore(
    subscribe,
    () => (sessionId === null ? undefined : listsFor(sessionId).find(client, sessionId)),
    () => undefined,
  );
}

/**
 * The session's list row's verdict on its work, where the row is newer than its cached detail
 * (`listedWork`). One value per row and detail, so a memo keyed on it holds between renders.
 */
export function useListedWork(
  sessionId: string,
  detail: SessionResponse | null | undefined,
): ListedWork | undefined {
  const listed = useListedSession(sessionId);
  const session = detail?.session;
  return useMemo(() => listedWork(listed, session), [listed, session]);
}

/**
 * Observe the composer's cached activity without fetching or resuming sidebar sessions.
 * Realtime reaches only the open session, so a session's cached detail can miss the turn
 * ending that its list row saw, the open one's too when it was left mid-turn.
 */
export function useSessionRunning(sessionId: string, fallbackRunning: boolean): boolean {
  const client = useQueryClient();
  const { data } = useQuery({ ...codeSessionOptions(sessionId), enabled: false });
  const listed = useListedSession(sessionId);
  const controller = useMemo(
    () => getCodeSessionController(client, sessionId),
    [client, sessionId],
  );
  const transport = useSyncExternalStore(
    controller.subscribe,
    controller.getSnapshot,
    controller.getSnapshot,
  );
  return isSessionRunning(data, transport, fallbackRunning, listed);
}
